import React, { useEffect, useRef, useState } from 'react'
import { MoreVertical } from 'lucide-react'
import { cn } from '../../lib/utils'

// Collapses inline row icons (edit + delete + occasional "mark
// achieved", etc.) into a single ⋯ menu. Reduces accidental taps —
// the previous layout put a pencil icon two pixels from a trash icon,
// and a mis-tap on the pencil meant the user "added" a duplicate but
// actually edited the existing row, silently overwriting it.
//
// Props:
//   actions: Array<{ icon?, label: string, onClick: () => void,
//                    variant?: 'default' | 'destructive' }>
//   align: 'end' (default) | 'start' — which side the menu opens from
export function RowActionsMenu({ actions, align = 'end', ariaLabel }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('touchstart', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('touchstart', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!actions || actions.length === 0) return null

  return (
    <div ref={wrapRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel || 'Actions'}
        className="p-1.5 -m-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div
          role="menu"
          className={cn(
            'absolute top-full mt-1 z-30 min-w-[140px] rounded-lg overflow-hidden',
            'bg-card border border-border shadow-lg',
            'animate-in fade-in slide-in-from-top-1',
            align === 'end' ? 'end-0' : 'start-0',
          )}
        >
          {actions.map((a, i) => {
            const Icon = a.icon
            return (
              <button
                key={i}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  a.onClick()
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm text-start transition-colors',
                  a.variant === 'destructive'
                    ? 'text-red-600 hover:bg-red-500/10'
                    : 'text-foreground hover:bg-accent',
                  i > 0 && 'border-t border-border/40',
                )}
              >
                {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
                <span dir="auto" className="truncate">{a.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
